<template>
  <div class="flex flex-col gap-6">
    <!-- Guidance (#251): accompagne la sélection en fin de création de mission (sélection + comparaison) -->
    <b-expert-selection-help variant="wizard" data-e2e="step5-selection-help" />

    <!-- Search Section -->
    <div>
      <h3 class="text-white font-medium mb-2">{{ $t('publish_mission.step5.search_label') }}</h3>
      <b-combobox
        name="expert_search"
        :placeholder="$t('publish_mission.step5.search_placeholder')"
        v-model="searchSelection"
        :items="searchResults"
        :min-search-length="2"
        @search="onExpertSearch"
        @update:model-value="onExpertSelected"
        data-e2e="step5-expert-search"
      />
    </div>

    <!-- Selected Experts -->
    <div v-if="selectedExperts.length > 0">
      <div class="flex items-center justify-between mb-3 gap-3">
        <b-label class="block">
          {{ $t('publish_mission.step5.selected_experts') }} ({{ selectedExperts.length }})
        </b-label>
        <button
          type="button"
          class="text-sm font-medium px-4 py-2 rounded-lg bg-primary text-white disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
          :disabled="selectedExperts.length < 2"
          data-e2e="step5-compare-button"
          @click="openComparison"
        >
          {{ $t('expert_comparison.compare_action') }}
        </button>
      </div>
      <div class="flex flex-wrap gap-3">
        <div
          v-for="expert in selectedExperts"
          :key="expert.value"
          class="flex items-center gap-2 bg-gray-800 rounded-lg px-3 py-2"
        >
          <div class="size-8 rounded-full bg-gray-700 overflow-hidden">
            <img
              v-if="expert.picture_url"
              :src="expert.picture_url"
              :alt="expert.label"
              class="size-full object-cover"
            />
            <div v-else class="size-full flex items-center justify-center text-gray-400 text-sm">
              {{ getInitials(expert.label) }}
            </div>
          </div>
          <span class="text-white text-sm">{{ expert.label }}</span>
          <button
            type="button"
            class="ml-1 text-gray-400 hover:text-red-500 transition-colors"
            @click="removeExpert(expert.value)"
            :data-e2e="`step5-remove-expert-${expert.value}`"
          >
            <Icon name="solar:close-circle-linear" class="size-4" />
          </button>
        </div>
      </div>
    </div>

    <!-- Matching Experts Section - TABLE FORMAT -->
    <div>
      <h3 class="text-white font-medium mb-2">{{ $t('publish_mission.step5.matching_experts') }}</h3>

      <div v-if="loadingMatches" class="flex items-center justify-center py-8">
        <div class="animate-spin rounded-full size-8 border-b-2 border-primary" />
      </div>

      <div v-else-if="matchingExperts.length === 0" class="text-center py-8">
        <Icon name="solar:users-group-two-rounded-linear" class="size-12 text-gray-600 mx-auto mb-3" />
        <p class="text-gray-400">{{ $t('publish_mission.step5.no_matches') }}</p>
      </div>

      <div v-else>
        <!-- Table -->
        <div class="bg-gray-800 rounded-xl overflow-hidden">
          <table class="w-full">
            <thead>
              <tr class="border-b border-gray-700">
                <th class="px-4 py-3 text-left w-12">
                  <span class="sr-only">{{ $t('publish_mission.step5.table_select') }}</span>
                </th>
                <th class="px-4 py-3 text-left text-gray-400 text-sm font-medium">
                  {{ $t('publish_mission.step5.table_expert') }}
                </th>
                <th class="px-4 py-3 text-left text-gray-400 text-sm font-medium w-48">
                  {{ $t('publish_mission.step5.table_matching') }}
                </th>
                <th class="px-4 py-3 w-12">
                  <span class="sr-only">{{ $t('publish_mission.step5.table_actions') }}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-for="expert in matchingExperts" :key="expert.id">
                <!-- Main row -->
                <tr
                  class="border-b border-gray-700 last:border-b-0 hover:bg-gray-750 transition-colors cursor-pointer"
                  :class="{ 'border-b-0': expandedRowId === expert.id }"
                  @click="toggleExpand(expert.id)"
                  :data-e2e="`step5-matching-expert-${expert.id}`"
                >
                  <!-- Checkbox -->
                  <td class="px-4 py-3" @click.stop="toggleExpert(expert)">
                    <div
                      class="size-5 rounded border-2 flex items-center justify-center transition-colors cursor-pointer"
                      :class="isSelected(expert.id)
                        ? 'bg-primary border-primary'
                        : 'border-gray-600 bg-transparent'"
                    >
                      <Icon
                        v-if="isSelected(expert.id)"
                        name="solar:check-read-linear"
                        class="size-3.5 text-white"
                      />
                    </div>
                  </td>

                  <!-- Avatar + Name -->
                  <td class="px-4 py-3">
                    <div class="flex items-center gap-3">
                      <div class="size-10 rounded-full bg-gray-700 overflow-hidden flex-shrink-0">
                        <img
                          v-if="expert.signed_picture_url"
                          :src="expert.signed_picture_url"
                          :alt="`${expert.first_name} ${expert.last_name}`"
                          class="size-full object-cover"
                        />
                        <div v-else class="size-full flex items-center justify-center text-gray-400 text-sm">
                          {{ getInitials(`${expert.first_name} ${expert.last_name}`) }}
                        </div>
                      </div>
                      <div class="flex flex-col gap-1 min-w-0">
                        <a
                          v-if="expert.handle"
                          :href="`${config.public.NUXT_APP_URL}account/${expert.handle}${props.mission?.id ? `?mission=${props.mission.id}` : ''}`"
                          target="_blank"
                          class="text-white font-medium underline hover:text-primary transition-colors"
                          @click.stop
                        >
                          {{ expert.first_name }} {{ expert.last_name }}
                        </a>
                        <span v-else class="text-white font-medium">{{ expert.first_name }} {{ expert.last_name }}</span>
                        <div v-if="orderedLanguages(expert).length" class="flex flex-wrap gap-1">
                          <b-badge v-for="code in orderedLanguages(expert)" :key="code" pill variant="skill">{{ $t('languages.' + code) }}</b-badge>
                        </div>
                      </div>
                    </div>
                  </td>

                  <!-- Matching % with Progress Bar -->
                  <td class="px-4 py-3">
                    <div class="flex items-center gap-3">
                      <div class="h-2 flex-1 bg-gray-700 rounded-full overflow-hidden">
                        <div
                          class="h-full bg-primary rounded-full transition-all"
                          :style="{ width: `${expert.match_score || 0}%` }"
                        />
                      </div>
                      <span class="text-primary text-sm font-medium min-w-[40px] text-right">
                        {{ expert.match_score || 0 }}%
                      </span>
                    </div>
                  </td>

                  <!-- Expand/Collapse Chevron -->
                  <td class="px-4 py-3">
                    <Icon
                      :name="expandedRowId === expert.id ? 'solar:alt-arrow-up-linear' : 'solar:alt-arrow-down-linear'"
                      class="size-4 text-gray-400"
                    />
                  </td>
                </tr>

                <!-- Expandable Score Breakdown Row -->
                <tr v-if="expandedRowId === expert.id && expert.score_breakdown">
                  <td></td>
                  <td colspan="3" class="pl-4 pr-4 pb-4">
                    <BScoreTooltip
                      :breakdown="expert.score_breakdown"
                      :match-score="expert.match_score"
                      class="!w-full !bg-transparent !border-0 !shadow-none !p-0"
                    />
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <b-table-pagination-numeric
          v-if="totalMatches > PAGE_SIZE"
          :total="totalMatches"
          :limit="PAGE_SIZE"
          :current="currentPage"
          @change-page="onPageChange"
        />
      </div>
    </div>

    <!-- External Share Section -->
    <div class="border-t border-gray-700 pt-6">
      <b-share-mission :mission="mission" />
    </div>

    <!-- Expert comparison modal (déclenchée par le bouton "Comparer" ci-dessus) -->
    <b-compare-experts-modal
      :open="comparison.isOpen"
      :loading="comparison.loading"
      :error="comparison.error"
      :data="comparison.data"
      :context="comparison.context"
      @close="comparison.close"
      @retry="comparison.retry"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import BLabel from '~/components/forms/BLabel.vue';
import BCombobox, { type ComboboxItem } from '~/components/forms/BCombobox.vue';
import BShareMission from '~/components/mission/BShareMission.vue';
import BTablePaginationNumeric from '~/components/table/BTablePaginationNumeric.vue';
import BScoreTooltip from '~/components/tooltip/BScoreTooltip.vue';
import type { MissionResponse } from '~/api/v2/missions/MissionRepository';
import type { ExpertByMissionResult } from '~/api/v2/experts/ExpertRepository';
import { useExpertComparison } from '~/composables/useExpertComparison';
import BCompareExpertsModal from '~/components/modal/expert/BCompareExpertsModal.vue';
import BExpertSelectionHelp from '~/components/expert/BExpertSelectionHelp.vue';
import BBadge from '~/components/badge/BBadge.vue';
import { orderedSpokenLanguages } from '~/help/languages';

const userStore = useUserStore();
const auth = authStore();
const config = useRuntimeConfig();

interface SelectedExpert {
  value: string;
  label: string;
  picture_url?: string;
}

interface Props {
  mission?: MissionResponse | null;
}

const props = defineProps<Props>();

const selectedExperts = defineModel<SelectedExpert[]>('selectedExperts', { default: [] });

// Expert comparison : pilotée par la sélection d'invitation existante.
// Le bouton "Comparer" n'est actif que lorsque exactement 2 experts sont sélectionnés.
const comparison = useExpertComparison({
  context: 'mission-wizard',
  missionId: () => props.mission?.id ?? undefined,
});

function openComparison() {
  comparison.openWith(selectedExperts.value.map((e) => e.value));
}

const searchSelection = ref<ComboboxItem | null>(null);
const searchResults = ref<ComboboxItem[]>([]);
const matchingExperts = ref<ExpertByMissionResult[]>([]);
const loadingMatches = ref(false);
const totalMatches = ref(0);
const currentPage = ref(1);
const PAGE_SIZE = 5;
const expandedRowId = ref<string | null>(null);

const orderedLanguages = (expert: ExpertByMissionResult) => orderedSpokenLanguages(expert.languages);

const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const isSelected = (expertId: string): boolean => {
  return selectedExperts.value.some(e => e.value === expertId);
};

const toggleExpert = (expert: ExpertByMissionResult) => {
  if (isSelected(expert.id)) {
    removeExpert(expert.id);
  } else {
    selectedExperts.value = [
      ...selectedExperts.value,
      {
        value: expert.id,
        label: `${expert.first_name} ${expert.last_name}`.trim(),
        picture_url: expert.signed_picture_url,
      },
    ];
  }
};

const toggleExpand = (expertId: string) => {
  expandedRowId.value = expandedRowId.value === expertId ? null : expertId;
};

const removeExpert = (expertId: string) => {
  selectedExperts.value = selectedExperts.value.filter(e => e.value !== expertId);
};

const onExpertSearch = async (query: string) => {
  if (query.length < 2) {
    searchResults.value = [];
    return;
  }

  try {
    const response = await userStore.fetchUsers({ 'filter[q]': query, limit: 10 });
    searchResults.value = response.data
      .filter(user => user.id !== auth.user?.id)
      .map(user => ({
        label: `${user.first_name} ${user.last_name}`,
        value: user.id,
      }));
  } catch (error) {
    console.error('Error searching experts:', error);
    searchResults.value = [];
  }
};

const onExpertSelected = (item: ComboboxItem | null) => {
  if (item && !isSelected(item.value as string)) {
    selectedExperts.value = [
      ...selectedExperts.value,
      {
        value: item.value as string,
        label: item.label,
      },
    ];
  }
  searchSelection.value = null;
};

const fetchMatchingExperts = async (page = 1) => {
  if (!props.mission?.id) return;

  loadingMatches.value = true;
  try {
    const { $api } = useNuxtApp();
    const offset = (page - 1) * PAGE_SIZE;
    const response = await $api().v2.experts.searchByMission(props.mission.id, { limit: PAGE_SIZE, offset });
    matchingExperts.value = response.data;
    totalMatches.value = response.total;
    currentPage.value = page;
  } catch (error) {
    console.error('Error fetching matching experts:', error);
    matchingExperts.value = [];
    totalMatches.value = 0;
  } finally {
    loadingMatches.value = false;
  }
};

const onPageChange = (page: number) => {
  fetchMatchingExperts(page);
};

watch(() => props.mission, (newMission) => {
  if (newMission) {
    fetchMatchingExperts();
  }
}, { immediate: true });

onMounted(() => {
  fetchMatchingExperts();
});
</script>

<style scoped>
.bg-gray-750 {
  background-color: rgb(38 38 45);
}
</style>
